// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-nocheck
import { TenantBaseApi } from "api/TenantSlug/TenantBaseApi.ts";

import GetIntegrationsResponse = App.Http.Responses.Integration.GetIntegrationsResponse;
import GetConnectionSequenceStepsExportResponse = App.Http.Responses.ConnectionCreator.SequenceStep.GetConnectionSequenceStepsExportResponse;
import GetIntegrationsRequest = App.Http.Requests.Integration.GetIntegrationsRequest;
import GetIntegrationResponse = App.Http.Responses.Integration.GetIntegrationResponse;
import StoreIntegrationRequest = App.Http.Requests.Integration.StoreIntegrationRequest;
import PatchIntegrationRequest = App.Http.Requests.Integration.PatchIntegrationRequest;
import StoreIntegrationConnectionSequenceStepsRequest = App.Http.Requests.Integration.StoreIntegrationConnectionSequenceStepsRequest;
import BaseExportRequest = App.Http.Requests.BaseExportRequest;
import ExportResponse = App.Http.Responses.ExportResponse;
import TestIntegrationRequest = App.Http.Requests.Integration.TestIntegrationRequest;
import TestConnectionSequenceStepResponse = App.Http.Responses.ConnectionSequenceStep.TestConnectionSequenceStepResponse;

class IntegrationApi extends TenantBaseApi {
    /**
     * Get list of integrations
     *
     * - Request: GetIntegrationsRequest
     * - Response: GetIntegrationsResponse
     */
    async index(request: GetIntegrationsRequest): Promise<GetIntegrationsResponse> {
        return await this.get<GetIntegrationsResponse, GetIntegrationsRequest>("/integrations", {
            params: request,
        });
    }

    /**
     * Store Integration
     *
     * - Request: StoreIntegrationRequest
     * - Response: GetIntegrationResponse
     */
    async store(request: StoreIntegrationRequest): Promise<GetIntegrationResponse> {
        return await this.post<GetIntegrationResponse, StoreIntegrationRequest>("/integrations", request);
    }

    /**
     * Get specific integration
     *
     * - Request: { id: string }
     * - Response: GetIntegrationResponse
     */
    async show(integrationId: string, request?: GetIntegrationsRequest): Promise<GetIntegrationResponse> {
        return await this.get<GetIntegrationResponse, GetIntegrationsRequest>(`/integrations/${integrationId}`, {
            params: request,
        });
    }

    /**
     * Get specific integration in export format
     *
     * - Request: { id: string }
     * - Response: GetIntegrationResponse
     */
    async showExport(integrationId: string, request?: GetIntegrationsRequest): Promise<GetIntegrationResponse> {
        return await this.get<GetIntegrationResponse, GetIntegrationsRequest>(`/integrations/${integrationId}/export`, {
            params: request,
        });
    }

    /**
     * Get specific integration connection sequence steps in export format
     *
     * - Request: { id: string }
     * - Response: GetIntegrationExportResponse
     */
    async showMethodsExport(
        integrationId: string,
        request?: GetIntegrationsRequest
    ): Promise<GetConnectionSequenceStepsExportResponse> {
        return await this.get<GetConnectionSequenceStepsExportResponse, GetIntegrationsRequest>(
            `/integrations/${integrationId}/connection-sequence-steps/export`,
            {
                params: request,
            }
        );
    }

    /**
     * Update Integration (PUT)
     *
     * - Request: StoreIntegrationRequest
     * - Response: GetIntegrationResponse
     */
    async update(request: StoreIntegrationRequest, integrationId: string): Promise<GetIntegrationResponse> {
        return await this.put<GetIntegrationResponse, StoreIntegrationRequest>(
            `/integrations/${integrationId}`,
            request
        );
    }

    /**
     * Update Integration methods (PUT)
     *
     * - Request: StoreIntegrationConnectionSequenceStepsRequest
     * - Response: GetConnectionSequenceStepsExportResponse
     */
    async updateMethods(
        request: StoreIntegrationConnectionSequenceStepsRequest,
        integrationId: string
    ): Promise<GetConnectionSequenceStepsExportResponse> {
        return await this.put<GetConnectionSequenceStepsExportResponse, StoreIntegrationConnectionSequenceStepsRequest>(
            `/integrations/${integrationId}/connection-sequence-steps`,
            request
        );
    }

    /**
     * Partially update Integration (PATCH)
     *
     * - Request: PatchIntegrationRequest
     * - Response: GetIntegrationResponse
     */
    async patchUpdate(request: PatchIntegrationRequest, integrationId: string): Promise<GetIntegrationResponse> {
        return await this.patch<GetIntegrationResponse, PatchIntegrationRequest>(
            `/integrations/${integrationId}`,
            request
        );
    }

    /**
     * Delete Integration
     *
     * - Request: { id: string }
     * - Response: void
     */
    async destroy(integrationId: string): Promise<void> {
        return await this.delete<void>(`/integrations/${integrationId}`);
    }

    async export(request: BaseExportRequest): Promise<ExportResponse> {
        return await this.post<ExportResponse, BaseExportRequest>("/integrations/export", request);
    }

    async test(integrationId: string, request: TestIntegrationRequest): Promise<TestConnectionSequenceStepResponse> {
        return await this.post<TestConnectionSequenceStepResponse, TestIntegrationRequest>(
            `/integrations/${integrationId}/test`,
            request
        );
    }
}

export const integrationApi = new IntegrationApi();
